import {
    Dropdown,
    DropdownChangeEvent,
    DropdownFilterEvent,
    DropdownProps,
} from 'primereact/dropdown';
import classes from './DropdownField.module.scss';
import React from 'react';

interface DropdownFieldProps extends Omit<DropdownProps, 'onChange' | 'value' | 'options'> {
    label?: string;
    name: string;
    value: DropdownProps['value'];
    onChange: (e: DropdownChangeEvent) => void;
    onFilter?: (e: DropdownFilterEvent) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: string;
    isRequired?: boolean;
    options: NonNullable<DropdownProps['options']>;
    placeholder?: string;
    disabled?: boolean;
    showClear?: boolean;
    filter?: boolean;
    className?: string;
    wrapperClassName?: string;
    optionValue?: string;
    optionLabel?: string;
    showLabel?: boolean;
    emptyMessage?: string | React.ReactNode;
    showEmptyMessage?: boolean;
}

/** Отображает поле выбора с подписью и ошибкой. */
const DropdownComponent: React.FC<DropdownFieldProps> = (
    {
        label,
        name,
        value,
        onChange,
        onFilter,
        onBlur,
        error,
        isRequired = false,
        options,
        placeholder = "Выберите значение",
        disabled = false,
        showClear = true,
        filter = false,
        className = '',
        wrapperClassName = '',
        optionValue = 'id',
        optionLabel = 'name',
        showLabel = true,
        emptyMessage = "Ничего не найдено",
        showEmptyMessage = true,
        ...dropdownProps
    }
) => {
    const labelClass = `${classes.label} ${isRequired
        ? classes.required
        : ''}`;

    return (
        <div className={`${classes.wrapper} ${wrapperClassName}`}>
            {showLabel && label && (
                <label
                    htmlFor={name}
                    className={labelClass}
                >
                    {label}
                </label>
            )}
            <Dropdown
                id={name}
                inputId={name}
                ariaLabel={label}
                aria-label={label}
                pt={{
                    select: { 'aria-label': label },
                    root: { 'aria-activedescendant': undefined }
                }}
                name={name}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                options={options}
                optionValue={optionValue}
                optionLabel={optionLabel}
                placeholder={placeholder}
                disabled={disabled}
                showClear={showClear}
                filter={filter}
                onFilter={onFilter}
                className={`${classes.dropdown} ${error
                    ? classes.error
                    : ''} ${className}`}
                emptyMessage={showEmptyMessage
                    ? emptyMessage
                    : undefined
                }
                aria-invalid={Boolean(error)}
                aria-describedby={error
                    ? `${name}-error`
                    : undefined
                }
                {...dropdownProps}
            />
            {error && (
                <small
                    id={`${name}-error`}
                    className={classes.errorText}
                    role="alert"
                >
                    {error}
                </small>
            )}
        </div>
    );
};

export const DropdownField = React.memo(DropdownComponent, (prevProps, nextProps) => {
    const fieldStateChanged = prevProps.value !== nextProps.value
        || prevProps.error !== nextProps.error
        || prevProps.disabled !== nextProps.disabled
        || prevProps.options !== nextProps.options;

    if (
        fieldStateChanged
    ) {
        return false;
    }

    const handlersChanged = prevProps.onChange !== nextProps.onChange
        || prevProps.onBlur !== nextProps.onBlur
        || prevProps.onFilter !== nextProps.onFilter;

    if (
        handlersChanged
    ) {
        return false;
    }

    return true;
});

DropdownField.displayName = 'DropdownField';
